import { Badge, OptionCard, RadioGroup } from '@/components/ui';
import { formatPaise, formatSheets } from '@/lib/format';

import { cheapestSlug, type PresetChoice } from './presets';

export interface PresetPickerProps {
  choices: PresetChoice[];
  /** Slug of the selected preset. */
  value: string | null;
  onChange: (slug: string) => void;
  /** True while a new set of prices is in flight; dims the numbers, keeps the layout. */
  refreshing?: boolean;
}

/**
 * The preset list, with a live printing cost on every card.
 *
 * The reason each card carries its OWN number rather than a "+₹20" delta
 * against the selection: the brief's actual question is "is double-sided
 * cheaper?", and a delta only answers that relative to whatever happens to be
 * selected. Four absolute numbers answer it at a glance, in any order.
 *
 * The number is printing ALONE — pages x copies x the per-page rate. It used to
 * be the delivered total, which had the merit of never rising later, but it
 * made twelve pages at a rupee a page read "₹20.06" and no customer could find
 * their own arithmetic in it. Printing alone is the figure they came to check.
 * The cost is real: the total at the pay button is higher, so the hint says so
 * before they get there rather than after.
 *
 * Sheets are on the card too, because that is the physical fact behind the
 * saving — "5 sheets" next to "10 sheets" explains the price difference better
 * than the price difference does.
 *
 * DENSE BY CONSTRUCTION. Every file's options panel is open from the start, so
 * four full-height cards per document would be a screen of scrolling before
 * the second file. The preset's own blurb is what that costs: the label names
 * the option, and the sheets and the rate are what a customer compares on.
 */
export function PresetPicker({ choices, value, onChange, refreshing = false }: PresetPickerProps) {
  const cheapest = cheapestSlug(choices);

  return (
    <RadioGroup
      legend="How should we print it?"
      // Printing alone, so the arithmetic on the card is the arithmetic the
      // customer can do in their head: 12 pages at a rupee is twelve rupees.
      // What that costs is that this is NOT the number they pay, so the line
      // below has to say so plainly — a price that grows at the pay button is
      // the commonest reason a checkout is abandoned.
      hint="Printing only. Handling, GST and delivery are added at checkout."
      className="gap-1.5"
      compact
    >
      {choices.map((choice) => {
        const isCheapest = choice.slug === cheapest && choices.length > 1;

        return (
          <OptionCard
            key={choice.id}
            name="print-preset"
            value={choice.slug}
            checked={value === choice.slug}
            onChange={() => onChange(choice.slug)}
            compact
            label={
              <span className="flex flex-wrap items-center gap-2">
                {choice.label}
                {isCheapest ? (
                  <Badge tone="success" srPrefix="This option is">
                    Cheapest
                  </Badge>
                ) : null}
              </span>
            }
            description={
              <>
                {formatSheets(choice.sheets)} &middot; {formatPaise(choice.pricePerPagePaise)} per
                page
                {/* On the card as well as in the notice above, because the
                    cards can differ: if one preset stocks the customer's size
                    and another does not, a single notice at the top cannot say
                    which is which. Kept to three words — the notice carries the
                    explanation, this only says which paper THIS preset uses. */}
                {choice.documentSizeLabel ? (
                  <span className="block">Printed on {choice.sizeLabel}.</span>
                ) : null}
              </>
            }
            meta={
              <span className={refreshing ? 'opacity-50 transition-opacity' : 'transition-opacity'}>
                {formatPaise(choice.printingCostPaise)}
              </span>
            }
          />
        );
      })}
    </RadioGroup>
  );
}
